import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import FestiveAfternoonTeaEntryModal, {
  FESTIVE_TEA_BOOKING,
} from './FestiveAfternoonTeaEntryModal';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const heroImage = 'https://BelvoirCastle.b-cdn.net/Christmas/tea1.webp';
const tea2Image = 'https://BelvoirCastle.b-cdn.net/Christmas/tea2.webp';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2.25rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 1.4rem;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const Notice = styled.div`
  background: rgba(201,150,60,0.08);
  border-left: 4px solid ${C.gold};
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${C.navy};
  font-weight: 600;
  margin-top: 1.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const ImgStrip = styled.div`
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

const HeroBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    a { width: 100%; text-align: center; }
  }
`;

const BtnPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const BtnGhost = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.65);
  background: rgba(255,255,255,0.07);
  color: #fff;
  text-decoration: none;
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: clamp(2rem, 4vw, 3rem);
  @media (max-width: 860px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  background: ${p => p.$bg};
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  color: #fff;
`;

const StepNum = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 2.2rem;
  font-weight: 700;
  color: rgba(255,255,255,0.22);
  line-height: 1;
  margin-bottom: 0.7rem;
`;

const StepTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
`;

const StepBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  max-width: 820px;
`;

const FaqItem = styled.details`
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 1rem 1.25rem;

  summary {
    cursor: pointer;
    font-family: ${p => p.theme.fonts.main};
    font-size: 1.1rem;
    font-weight: 700;
    color: ${C.navy};
    list-style: none;
  }

  summary::-webkit-details-marker { display: none; }

  p {
    margin: 0.75rem 0 0;
    font-family: 'Inter', -apple-system, sans-serif;
    font-size: 0.92rem;
    line-height: 1.65;
    color: ${C.muted};
  }
`;

const TEA_BOOKING_URL = FESTIVE_TEA_BOOKING.castle;
const PAGE_URL = 'https://www.belvoircastle.com/christmas/festive-afternoon-tea';
const OG_IMAGE = 'https://www.belvoircastle.com/christmas25/tea1.webp';
const BOOKING_ABSOLUTE = `https://www.belvoircastle.com${TEA_BOOKING_URL}`;

const FAQS = [
  {
    question: 'Why is Belvoir Castle known as the home of afternoon tea?',
    answer:
      'Belvoir Castle is widely regarded as the home of afternoon tea — a tradition of elegant tearoom hospitality in one of England\'s finest historic castles. Year-round in the Aviary Tearoom, and at Christmas as Festive Afternoon Tea during Deck the Halls, Belvoir offers one of the most celebrated afternoon tea experiences in Leicestershire and the East Midlands.',
  },
  {
    question: 'Is Festive Afternoon Tea included with Deck the Halls Castle entry?',
    answer:
      'No. Festive Afternoon Tea is an add-on and is not included with Castle or Gardens entry. Book Castle entry or Gardens & Playground entry first, then add afternoon tea at checkout and choose your sitting.',
  },
  {
    question: 'Where is Festive Afternoon Tea served at Belvoir Castle?',
    answer:
      'Festive Afternoon Tea is served in the Aviary Tearoom inside Belvoir Castle during Deck the Halls, surrounded by the Castle\'s carol-themed Christmas rooms — the same historic tearoom that makes Belvoir the home of afternoon tea.',
  },
  {
    question: 'When can I book Festive Afternoon Tea for Christmas 2026?',
    answer:
      'Festive Afternoon Tea is available during Deck the Halls 2026 (14 November – 30 December). Sittings are selected when you add tea to your booking at checkout. Pre-booking is recommended as spaces are limited.',
  },
  {
    question: 'What is included in Festive Afternoon Tea?',
    answer:
      'A curated selection of seasonal savouries, delicate festive patisserie, freshly baked scones with clotted cream and preserves, and fine teas — served with Belvoir\'s signature tearoom hospitality in the Aviary Tearoom.',
  },
  {
    question: 'Can I book Festive Afternoon Tea without entry?',
    answer:
      'Festive Afternoon Tea is booked as an add-on to Castle entry or Gardens & Playground entry. Choose your entry type when you book, then add afternoon tea on the extras screen — you cannot add more teas than entry tickets in your party. Available 14 November – 30 December 2026.',
  },
  {
    question: 'How do I share dietary requirements or allergens?',
    answer:
      'When you add Festive Afternoon Tea at checkout and choose your sitting, you can share allergen or dietary notes for each guest so the Aviary Tearoom can prepare accordingly.',
  },
  {
    question: 'Is afternoon tea available at Belvoir Castle outside Christmas?',
    answer:
      'Yes. Belvoir Castle serves afternoon tea year-round in the Aviary Tearoom. Festive Afternoon Tea is the Christmas edition during Deck the Halls — the same home of afternoon tea, dressed for the festive season. See castle-dining/afternoon-tea for the year-round experience.',
  },
];

const FestiveAfternoonTea = () => {
  const [showEntryModal, setShowEntryModal] = useState(false);
  const openEntryModal = (e) => {
    if (e) e.preventDefault();
    setShowEntryModal(true);
  };
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';

  const meta = {
    title: 'Festive Afternoon Tea at Belvoir Castle | Home of Afternoon Tea · Christmas 2026',
    description:
      'Belvoir Castle is the home of afternoon tea. Book Festive Afternoon Tea for Christmas 2026 in the Aviary Tearoom — seasonal savouries, festive patisserie and fine teas. Add-on to Castle or Gardens entry. Available 14 Nov – 30 Dec. Pre-book your sitting.',
  };

  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'FoodEvent',
    name: 'Festive Afternoon Tea at Belvoir Castle 2026',
    alternateName: [
      'Christmas Afternoon Tea Belvoir Castle',
      'Aviary Tearoom Festive Afternoon Tea',
      'Belvoir Castle Home of Afternoon Tea — Christmas',
    ],
    description: meta.description,
    image: [OG_IMAGE, 'https://www.belvoircastle.com/christmas25/tea2.webp'],
    startDate: '2026-11-14',
    endDate: '2026-12-30',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    url: PAGE_URL,
    keywords:
      'Festive Afternoon Tea, home of afternoon tea, Belvoir Castle afternoon tea, Christmas afternoon tea Leicestershire, Aviary Tearoom, Deck the Halls, best Christmas tea East Midlands, festive tea Grantham',
    location: {
      '@type': 'Place',
      name: 'Aviary Tearoom, Belvoir Castle',
      description:
        'Belvoir Castle — widely regarded as the home of afternoon tea — serves Festive Afternoon Tea in the historic Aviary Tearoom during Deck the Halls.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Belvoir Castle',
        addressLocality: 'Grantham',
        addressRegion: 'Leicestershire',
        postalCode: 'NG32 1PE',
        addressCountry: 'GB',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 52.8931,
        longitude: -0.7792,
      },
    },
    organizer: {
      '@type': 'Organization',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com',
      telephone: '+441476871001',
      email: 'info@belvoircastle.com',
    },
    offers: {
      '@type': 'Offer',
      url: BOOKING_ABSOLUTE,
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      validFrom: '2026-01-01',
      description:
        'Add-on to Deck the Halls Castle entry — book Castle tickets, then add Festive Afternoon Tea at checkout',
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Families, couples and groups visiting Deck the Halls',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.belvoircastle.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Christmas 2026',
        item: 'https://www.belvoircastle.com/christmas',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Festive Afternoon Tea',
        item: PAGE_URL,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: meta.title,
    description: meta.description,
    url: PAGE_URL,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com',
    },
    about: {
      '@type': 'Thing',
      name: 'Festive Afternoon Tea at Belvoir Castle',
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: OG_IMAGE,
    },
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta
          name="keywords"
          content="home of afternoon tea, Festive Afternoon Tea Belvoir Castle, Christmas afternoon tea Leicestershire, best afternoon tea East Midlands, Aviary Tearoom, Deck the Halls afternoon tea, Christmas tea Grantham, Belvoir Castle tea, festive tea Belvoir, Christmas dining castle, afternoon tea add-on, Christmas 2026 Belvoir Castle"
        />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="geo.region" content="GB-LEI" />
        <meta name="geo.placename" content="Grantham, Leicestershire" />
        <meta name="geo.position" content="52.8931;-0.7792" />
        <meta name="ICBM" content="52.8931, -0.7792" />
        <link rel="canonical" href={PAGE_URL} />
        <link
          rel="alternate"
          type="text/html"
          href="https://www.belvoircastle.com/festive-afternoon-tea.html"
          title="Festive Afternoon Tea — static AI-readable version"
        />
        <link rel="help" href="https://www.belvoircastle.com/llms.txt" title="Belvoir Castle AI-readable content index" />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Festive Afternoon Tea in the Aviary Tearoom — Belvoir Castle, home of afternoon tea" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Festive Afternoon Tea — Home of Afternoon Tea · Belvoir Castle Christmas 2026" />
        <meta name="twitter:description" content="Belvoir Castle is the home of afternoon tea. Festive Afternoon Tea in the Aviary Tearoom during Deck the Halls — seasonal savouries, patisserie and fine teas. Book your sitting." />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="Festive Afternoon Tea at Belvoir Castle Aviary Tearoom" />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={PAGE_URL} />

        <meta name="theme-color" content="#101d40" />
        <meta name="apple-mobile-web-app-title" content="Festive Afternoon Tea | Belvoir Castle" />

        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      </Helmet>

      <HeroWrap>
        <HeroImg src={heroImage} alt="Festive Afternoon Tea with seasonal treats in the Aviary Tearoom at Belvoir Castle — the home of afternoon tea, Christmas 2026" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>The Home of Afternoon Tea · Aviary Tearoom · Christmas 2026</HeroKicker>
          <HeroTitle>
            Festive <em>Afternoon Tea</em>
          </HeroTitle>
          <HeroStrap>
            Belvoir Castle is the home of afternoon tea — and at Christmas, that tradition becomes something truly magical
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Home of Afternoon Tea</Pill>
            <Pill>Now Booking</Pill>
            <Pill>Castle or Gardens Entry</Pill>
            <Pill>Aviary Tearoom</Pill>
          </BadgeRow>
          <HeroBtns>
            <BtnPrimary href={TEA_BOOKING_URL} onClick={openEntryModal}>Book Afternoon Tea</BtnPrimary>
            <BtnGhost href="/christmas/your-festive-visit">Plan Your Visit</BtnGhost>
          </HeroBtns>
        </HeroContent>
      </HeroWrap>

      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Home of Afternoon Tea</Kicker>
              <Divider />
              <SectionTitle>Christmas Indulgence<br /><em>in the Castle</em></SectionTitle>
              <SectionLead>
                Belvoir Castle is the home of afternoon tea — a reputation built on elegant tearoom hospitality, estate ingredients and the unmistakable setting of one of England&apos;s finest historic castles. At Christmas, that tradition becomes Festive Afternoon Tea in the Aviary Tearoom.
              </SectionLead>
              <SectionLead>
                Celebrate the season with seasonal savouries, delicate festive patisserie and freshly baked scones — served with fine teas in the warmth of our historic tearooms, surrounded by the magic of Deck the Halls.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Each grand room of Belvoir is dressed as a different Christmas carol. Pair your Castle visit with Festive Afternoon Tea for Leicestershire&apos;s most memorable Christmas tea — the home of afternoon tea, at its most festive.
              </SectionLead>
              <Notice>
                Festive Afternoon Tea is an add-on and is not included with entry. Choose Castle entry or Gardens &amp; Playground entry, then add afternoon tea at checkout — available 14 November – 30 December 2026.
              </Notice>
              <MidCta style={{ marginTop: '1.5rem' }}>
                <MidCtaText>Choose Castle or Gardens entry, then add Festive Afternoon Tea at checkout</MidCtaText>
                <MidCtaBtn href={TEA_BOOKING_URL} onClick={openEntryModal}>Book Now</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={tea2Image} alt="Festive Christmas afternoon tea with patisserie and fine teas at Belvoir Castle Aviary Tearoom — home of afternoon tea" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What to Expect</Kicker>
          <Divider />
          <SectionTitle>A Festive Tea <em>Like No Other</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>🏰</FeatureIcon>
              <FeatureTitle>The Home of Afternoon Tea</FeatureTitle>
              <FeatureBody>Belvoir Castle is celebrated as the home of afternoon tea — historic rooms, estate heritage and tearoom hospitality that guests travel across the East Midlands to experience.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🫖</FeatureIcon>
              <FeatureTitle>Fine Teas &amp; Seasonal Drinks</FeatureTitle>
              <FeatureBody>A carefully chosen selection of fine teas, served with warmth and elegance in the beautiful surroundings of the Aviary Tearoom at Belvoir Castle.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🍰</FeatureIcon>
              <FeatureTitle>Festive Patisserie</FeatureTitle>
              <FeatureBody>Indulgent seasonal sweet treats — delicate patisserie crafted for Christmas, alongside freshly baked scones with clotted cream and preserves.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🥪</FeatureIcon>
              <FeatureTitle>Seasonal Savouries</FeatureTitle>
              <FeatureBody>A curated selection of seasonal savoury bites — crafted from the finest local and estate ingredients, perfectly balanced with the sweet course.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎄</FeatureIcon>
              <FeatureTitle>Inside Deck the Halls</FeatureTitle>
              <FeatureBody>Your afternoon tea takes place inside Belvoir Castle during Deck the Halls — surrounded by grand rooms each dressed as a different Christmas carol.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon>🎟️</FeatureIcon>
              <FeatureTitle $light>Add to Your Visit</FeatureTitle>
              <FeatureBody $light>Choose Castle entry or Gardens &amp; Playground entry, then add Festive Afternoon Tea at checkout and choose your sitting. Entry tickets do not include tea — it must be added separately.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>How to Book</Kicker>
          <Divider />
          <SectionTitle>Four Steps to <em>Afternoon Tea</em></SectionTitle>
          <StepGrid>
            <Step $bg={C.navy}>
              <StepNum>01</StepNum>
              <StepTitle>Choose Castle or Gardens</StepTitle>
              <StepBody>Start with Castle entry or Gardens &amp; Playground entry. Afternoon tea is an extra — it is not included with general admission. Available 14 Nov – 30 Dec 2026.</StepBody>
            </Step>
            <Step $bg="#1a2d5a">
              <StepNum>02</StepNum>
              <StepTitle>Add Afternoon Tea</StepTitle>
              <StepBody>On the extras screen, add Festive Afternoon Tea for whoever wants it. You must add at least one tea from this page, but not more teas than entry tickets — others can order from the tearoom menu on the day.</StepBody>
            </Step>
            <Step $bg={C.gold}>
              <StepNum>03</StepNum>
              <StepTitle>Choose Your Sitting</StepTitle>
              <StepBody>Select your preferred afternoon tea time slot, then share any allergen or dietary notes for each guest.</StepBody>
            </Step>
            <Step $bg="#a07840">
              <StepNum>04</StepNum>
              <StepTitle>Arrive &amp; Enjoy</StepTitle>
              <StepBody>Explore Deck the Halls, then take your seats in the Aviary Tearoom at your chosen sitting.</StepBody>
            </Step>
          </StepGrid>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <InfoValue>During Deck<br />the Halls 2026</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Aviary Tearoom<br />Belvoir Castle</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <InfoValue>Add-on to<br />Castle Entry</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Time Slot</InfoLabel>
              <InfoValue>Selected<br />at Checkout</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Family Saver</InfoLabel>
              <InfoValue>Available on<br />Castle Entry</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>FAQs</Kicker>
          <Divider />
          <SectionTitle>Festive Afternoon Tea <em>Questions</em></SectionTitle>
          <SectionLead>
            Everything you need to know about booking Christmas afternoon tea at Belvoir Castle — the home of afternoon tea — in the Aviary Tearoom during Deck the Halls.
          </SectionLead>
          <FaqList>
            {FAQS.map(({ question, answer }) => (
              <FaqItem key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </FaqItem>
            ))}
          </FaqList>
        </Inner>
      </Section>

      <div id="newsletter" style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Book Your <em>Festive Tea</em>
          </CtaTitle>
          <CtaBody>
            Belvoir Castle is the home of afternoon tea. Festive Afternoon Tea is not included with entry — choose Castle or Gardens &amp; Playground entry, then add tea on the extras screen and choose your sitting (14 Nov – 30 Dec 2026).
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href={TEA_BOOKING_URL} onClick={openEntryModal}>
              Book Afternoon Tea
            </CtaBtn>
            <CtaSecondary to="/christmas/your-festive-visit">
              Plan Your Visit
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Home of Afternoon Tea · Aviary Tearoom · Castle or Gardens entry · 14 Nov – 30 Dec
          </CtaMeta>
        </CtaInner>
      </CtaSection>

      <FestiveAfternoonTeaEntryModal
        open={showEntryModal}
        onClose={() => setShowEntryModal(false)}
      />
      <Footer />
    </Page>
  );
};

export default FestiveAfternoonTea;
